import recoil from 'recoil';
import react from 'react';
import reactRelay from 'react-relay';
import relayRuntime from 'relay-runtime';

/**
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * @emails oncall+recoil
 * 
 * @format
 */

function err(message) {
  const error = new Error(message); // In V8, Error objects keep the closure scope chain alive until the
  // err.stack property is accessed.

  if (error.stack === undefined) {
    // IE sets the stack only if error is thrown
    try {
      throw error;
    } catch (_) {} // eslint-disable-line fb-www/no-unused-catch-bindings, no-empty

  }

  return error;
}

var err_1 = err;

// @oss-only


var Recoil_err = err_1;

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

const {
  useRecoilStoreID
} = recoil;



const {
  useEffect
} = react;

const {
  RelayEnvironmentProvider
} = reactRelay;



class EnvironmentKey {
  constructor(name) {
    _defineProperty(this, "_name", void 0);

    this._name = name;
  }

  toJSON() {
    return this._name;
  }

}

const environmentStore = new Map();
const cleanupHandlers = new Map();

function registerRelayEnvironment(storeID, environment, environmentKey) {
  var _environmentStore$get, _environmentStore$get2, _cleanupHandlers$get;

  if (!environmentStore.has(storeID)) {
    environmentStore.set(storeID, new Map());
  }

  const previousEnvironment = (_environmentStore$get = environmentStore.get(storeID)) === null || _environmentStore$get === void 0 ? void 0 : _environmentStore$get.get(environmentKey);

  if (previousEnvironment != null && previousEnvironment !== environment) {
    throw Recoil_err(`A consistent Relay environment should be used with the same Recoil store and EnvironmentKey "${environmentKey.toJSON()}"`);
  }

  (_environmentStore$get2 = environmentStore.get(storeID)) === null || _environmentStore$get2 === void 0 ? void 0 : _environmentStore$get2.set(environmentKey, environment); // Cleanup registered Relay Environments when they are no longer used to
  // avoid memory leaks.  However, defer it for an event look in case we are
  // running in <StrcitMode> which may call the effects, cleanup the effects,
  // and then call the effects of other components which try to re-query before
  // this effect to re-register gets re-called.  This should be safe because in
  // production the environment registered should never change.

  const pendingCleanup = (_cleanupHandlers$get = cleanupHandlers.get(storeID)) === null || _cleanupHandlers$get === void 0 ? void 0 : _cleanupHandlers$get.get(environmentKey);

  if (pendingCleanup != null) {
    var _cleanupHandlers$get2;

    clearTimeout(pendingCleanup);
    (_cleanupHandlers$get2 = cleanupHandlers.get(storeID)) === null || _cleanupHandlers$get2 === void 0 ? void 0 : _cleanupHandlers$get2.delete(environmentKey);
  }

  return () => {
    var _cleanupHandlers$get3, _cleanupHandlers$get4;

    const cleanupHandle = setTimeout(() => {
      var _environmentStore$get3;

      (_environmentStore$get3 = environmentStore.get(storeID)) === null || _environmentStore$get3 === void 0 ? void 0 : _environmentStore$get3.delete(environmentKey);
    }, 0);
    const oldHandler = (_cleanupHandlers$get3 = cleanupHandlers.get(storeID)) === null || _cleanupHandlers$get3 === void 0 ? void 0 : _cleanupHandlers$get3.get(environmentKey);

    if (oldHandler != null) {
      clearTimeout(oldHandler);
    }

    if (!cleanupHandlers.has(storeID)) {
      cleanupHandlers.set(storeID, new Map());
    }

    (_cleanupHandlers$get4 = cleanupHandlers.get(storeID)) === null || _cleanupHandlers$get4 === void 0 ? void 0 : _cleanupHandlers$get4.set(environmentKey, cleanupHandle);
  };
}
/**
 * @explorer-desc
 * Associates a RelayEnvironment with an EnvironmentKey for this <RecoilRoot>.
 */


function RecoilRelayEnvironment({
  environmentKey,
  environment,
  children
}) {
  const storeID = useRecoilStoreID();
  registerRelayEnvironment(storeID, environment, environmentKey); // Cleanup to avoid leaking retaining Relay Environments.

  useEffect(() => registerRelayEnvironment(storeID, environment, environmentKey), [storeID, environment, environmentKey]);
  return children;
}
/**
 * A provider which sets up the Relay environment for its children and
 * registers that environment for any Recoil atoms or selectors using that
 * environmentKey.
 *
 * This is basically a wrapper around both <RelayEnvironmentProvider> and
 * <RecoilRelayEnvironment>.
 */


function RecoilRelayEnvironmentProvider({
  environmentKey,
  environment,
  children
}) {
  return /*#__PURE__*/react.createElement(RecoilRelayEnvironment, {
    environmentKey: environmentKey,
    environment: environment
  }, /*#__PURE__*/react.createElement(RelayEnvironmentProvider, {
    environment: environment
  }, children));
}

function registerRecoilSnapshotRelayEnvironment(snapshot, environmentKey, environment) {
  const storeID = snapshot.getStoreID();
  return registerRelayEnvironment(storeID, environment, environmentKey);
}

function getRelayEnvironment(environmentOpt, storeID, parentStoreID) {
  if (environmentOpt instanceof EnvironmentKey) {
    var _environmentStore$get4, _environmentStore$get5, _environmentStore$get6;

    const environment = (_environmentStore$get4 = (_environmentStore$get5 = environmentStore.get(storeID)) === null || _environmentStore$get5 === void 0 ? void 0 : _environmentStore$get5.get(environmentOpt)) !== null && _environmentStore$get4 !== void 0 ? _environmentStore$get4 : parentStoreID != null ? (_environmentStore$get6 = environmentStore.get(parentStoreID)) === null || _environmentStore$get6 === void 0 ? void 0 : _environmentStore$get6.get(environmentOpt) : null;

    if (environment == null) {
      throw Recoil_err(`<RecoilRelayEnvironment> must be used at the top of your <RecoilRoot> with the same EnvironmentKey "${environmentOpt.toJSON()}" to register the Relay environment.`);
    }

    return environment;
  }

  return environmentOpt;
}

var RecoilRelay_Environments = {
  EnvironmentKey,
  RecoilRelayEnvironment,
  RecoilRelayEnvironmentProvider,
  registerRecoilSnapshotRelayEnvironment,
  getRelayEnvironment
};

var RecoilRelay_Environments_1 = RecoilRelay_Environments.EnvironmentKey;
var RecoilRelay_Environments_2 = RecoilRelay_Environments.RecoilRelayEnvironment;
var RecoilRelay_Environments_3 = RecoilRelay_Environments.RecoilRelayEnvironmentProvider;
var RecoilRelay_Environments_4 = RecoilRelay_Environments.registerRecoilSnapshotRelayEnvironment;
var RecoilRelay_Environments_5 = RecoilRelay_Environments.getRelayEnvironment;

var RecoilRelay_Environments$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  EnvironmentKey: RecoilRelay_Environments_1,
  RecoilRelayEnvironment: RecoilRelay_Environments_2,
  RecoilRelayEnvironmentProvider: RecoilRelay_Environments_3,
  registerRecoilSnapshotRelayEnvironment: RecoilRelay_Environments_4,
  getRelayEnvironment: RecoilRelay_Environments_5
});

/**
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * @emails oncall+recoil
 * 
 * @format
 */

function recoverableViolation(message, _projectName, {
  error
} = {}) {
  if (process.env.NODE_ENV !== "production") {
    console.error(message, error);
  }

  return null;
}

var recoverableViolation_1 = recoverableViolation;

// @oss-only


var Recoil_recoverableViolation = recoverableViolation_1;

const {
  getRelayEnvironment: getRelayEnvironment$1
} = RecoilRelay_Environments$1;

const {
  commitMutation
} = reactRelay;

 // TODO Use async atom support to set atom to error state.
// For now, log as a recoverableViolation() so errors aren't lost.


function logError(node, msg) {
  Recoil_recoverableViolation(`Error syncing atom "${node.key}" with GraphQL: ${msg}`);
}
/** graphQLMutationEffect()
 * Commit a GraphQL mutation each time the atom value is mutated.
 * - `environment`: The Relay Environment or an EnvironmentKey to match with
 *   the environment provided with `<RecoilRelayEnvironemnt>`.
 * - `mutation`: The GraphQL mutation.
 * - `variables`: Variables object provided as input to GraphQL mutation.  It is
 *   a callback that receives the updated atom value as the parameter.
 *   If null, then skip mutation.
 * - `updater`: Optional `updater()` function passed to `commitMutation()`.
 * - `optimisticUpdater`: Optional `optimisticUpdater()` function passed to `commitMutation()`.
 * - `optimisticResponse`: Optional optimistic response passed to `commitMutation()`.
 * - `uploadables`: Optional `uploadables` passed to `commitMutation()`.
 */


function graphQLMutationEffect({
  environment: environmentOpt,
  mutation,
  variables,
  updater_UNSTABLE: updater,
  optimisticUpdater_UNSTABLE: optimisticUpdater,
  optimisticResponse_UNSTABLE: optimisticResponse,
  uploadables_UNSTABLE: uploadables
}) {
  let currentMutationID = 0;
  return ({
    node,
    onSet,
    setSelf,
    storeID,
    parentStoreID_UNSTABLE
  }) => {
    const environment = getRelayEnvironment$1(environmentOpt, storeID, parentStoreID_UNSTABLE); // Local atom mutations will sync to update remote state
    // Treat as write-through cache, so local atom will update immediatly
    // and then write through to GraphQL mutation.

    onSet((newValue, oldValue) => {
      const mutationID = ++currentMutationID;
      const mutationVariables = variables(newValue);

      if (mutationVariables != null) {
        commitMutation(environment, {
          mutation,
          variables: mutationVariables,
          onError: error => {
            var _error$message;

            logError(node, (_error$message = error.message) !== null && _error$message !== void 0 ? _error$message : 'GraphQL Mutation'); // TODO, use logError() to set atom to error state instead?

            if (mutationID === currentMutationID) {
              setSelf(potentialyBadValue => // Avoid reverting value if atom was set in the meantime even if
              // the newer commitMutation() hasn't started yet.
              potentialyBadValue === newValue ? oldValue : potentialyBadValue);
            }
          },
          updater,
          optimisticUpdater,
          optimisticResponse: optimisticResponse === null || optimisticResponse === void 0 ? void 0 : optimisticResponse(newValue),
          uploadables
        });
      }
    });
  };
}

var RecoilRelay_graphQLMutationEffect = graphQLMutationEffect;

const {
  getRelayEnvironment: getRelayEnvironment$2
} = RecoilRelay_Environments$1;

const {
  fetchQuery
} = reactRelay;



const {
  createOperationDescriptor,
  getRequest,
  handlePotentialSnapshotErrors
} = relayRuntime; // TODO Use async atom support to set atom to error state.
// For now, log as a recoverableViolation() so errors aren't lost.


function logError$1(node, msg) {
  Recoil_recoverableViolation(`Error syncing atom "${node.key}" with GraphQL: ${msg}`);
}

function subscibeToLocalRelayCache(environment, query, variables, onNext) {
  const request = getRequest(query);
  const operation = createOperationDescriptor(request, variables);
  const operationDisposable = environment.retain(operation);
  const snapshot = environment.lookup(operation.fragment);
  const subscriptionDisposable = environment.subscribe(snapshot, newSnapshot => {
    handlePotentialSnapshotErrors(environment, newSnapshot.missingRequiredFields, newSnapshot.relayResolverErrors);

    if (!newSnapshot.isMissingData && newSnapshot.data != null) {
      // $FlowExpectedError[incompatible-call]
      onNext(newSnapshot.data);
    }
  });
  return () => {
    operationDisposable === null || operationDisposable === void 0 ? void 0 : operationDisposable.dispose();
    subscriptionDisposable === null || subscriptionDisposable === void 0 ? void 0 : subscriptionDisposable.dispose();
  };
}
/** graphQLQueryEffect()
 * Initialize an atom based on the results of a GraphQL query.
 * - `environment`: The Relay Environment or an EnvironmentKey to match with
 *   the environment provided with `<RecoilRelayEnvironemnt>`.
 * - `query`: The GraphQL query to query.
 * - `variables`: Variables object provided as input to GraphQL query.
 *   If null, then skip query and use default value.
 * - `mapResponse`: Callback to map the query response to the atom value.
 * - `subscribeToLocalMutations_UNSTABLE` - By default this effect will subscribe to
 *   mutations from local `commitMutation()` or `graphQLMutationEffect()` for the
 *   same part of the graph.  If you also need to subscribe to remote mutations,
 *   then use `graphQLSubscriptionEffect()`.
 */


function graphQLQueryEffect({
  environment: environmentOpt,
  query,
  variables,
  mapResponse,
  subscribeToLocalMutations_UNSTABLE = true
}) {
  return ({
    node,
    setSelf,
    trigger,
    storeID,
    parentStoreID_UNSTABLE
  }) => {
    if (variables == null) {
      return;
    }

    let querySubscription, localSubscriptionCleanup;
    const environment = getRelayEnvironment$2(environmentOpt, storeID, parentStoreID_UNSTABLE); // Initialize value

    if (trigger === 'get') {
      let initialResolve, initialReject;
      setSelf(new Promise((resolve, reject) => {
        initialResolve = resolve;
        initialReject = reject;
      }));
      querySubscription = fetchQuery(environment, query, variables, {
        fetchPolicy: 'store-or-network'
      }).subscribe({
        next: response => {
          const data = mapResponse(response);
          initialResolve(data);
          setSelf(data);
        },
        // TODO use Async atom support to set atom to error state on
        // subsequent errors during incremental updates.
        error: error => {
          var _error$message;

          initialReject(error);
          logError$1(node, (_error$message = error.message) !== null && _error$message !== void 0 ? _error$message : 'Error');
        }
      });
    } // Subscribe to local changes to update atom state.
    // To get remote mutations please use graphQLSubscriptionEffect()


    if (subscribeToLocalMutations_UNSTABLE) {
      localSubscriptionCleanup = subscibeToLocalRelayCache(environment, query, variables, data => setSelf(mapResponse(data)));
    }

    return () => {
      var _querySubscription, _localSubscriptionCle;

      (_querySubscription = querySubscription) === null || _querySubscription === void 0 ? void 0 : _querySubscription.unsubscribe();
      (_localSubscriptionCle = localSubscriptionCleanup) === null || _localSubscriptionCle === void 0 ? void 0 : _localSubscriptionCle();
    };
  };
}

var RecoilRelay_graphQLQueryEffect = graphQLQueryEffect;

const {
  getRelayEnvironment: getRelayEnvironment$3
} = RecoilRelay_Environments$1;

const {
  requestSubscription
} = reactRelay;

 // TODO Use async atom support to set atom to error state.
// For now, log as a recoverableViolation() so errors aren't lost.


function logError$2(node, msg) {
  Recoil_recoverableViolation(`Error syncing atom "${node.key}" with GraphQL: ${msg}`);
}
/** graphQLSubscriptionEffect()
 * Initialize and subscribe an atom to a GraphQL subscription.
 * - `environment`: The Relay Environment or an EnvironmentKey to match with
 *   the environment provided with `<RecoilRelayEnvironemnt>`.
 * - `subscription`: The GraphQL subscription to query.
 * - `variables`: Variables object provided as input to GraphQL subscription.
 *   If null, then skip subscription and use default value.
 * - `mapResponse`: Callback to map the subscription response to the atom value.
 */


function graphQLSubscriptionEffect({
  environment: environmentOpt,
  subscription,
  variables,
  mapResponse
}) {
  return ({
    node,
    setSelf,
    trigger,
    storeID,
    parentStoreID_UNSTABLE
  }) => {
    if (variables == null) {
      return;
    }

    const environment = getRelayEnvironment$3(environmentOpt, storeID, parentStoreID_UNSTABLE);
    let initialResolve, initialReject;

    if (trigger === 'get') {
      setSelf(new Promise((resolve, reject) => {
        initialResolve = resolve;
        initialReject = reject;
      }));
    } // Subscribe to remote changes to update atom state


    const graphQLSubscriptionDisposable = requestSubscription(environment, {
      subscription,
      variables,
      onNext: response => {
        if (response != null) {
          var _initialResolve;

          const data = mapResponse(response);
          (_initialResolve = initialResolve) === null || _initialResolve === void 0 ? void 0 : _initialResolve(data);
          setSelf(data);
        }
      },
      // TODO use Async atom support to set atom to error state on
      // subsequent errors during incremental updates.
      onError: error => {
        var _initialReject, _error$message;

        (_initialReject = initialReject) === null || _initialReject === void 0 ? void 0 : _initialReject(error);
        logError$2(node, (_error$message = error.message) !== null && _error$message !== void 0 ? _error$message : 'Error');
      }
    });
    return () => {
      graphQLSubscriptionDisposable.dispose();
    };
  };
}

var RecoilRelay_graphQLSubscriptionEffect = graphQLSubscriptionEffect;

function nullthrows(x, message) {
  if (x != null) {
    return x;
  }

  throw Recoil_err(message !== null && message !== void 0 ? message : 'Got unexpected null or undefined');
}

var Recoil_nullthrows = nullthrows;

const {
  DefaultValue,
  atomFamily,
  selectorFamily
} = recoil;








/**
 * graphQLSelectorFamily() implements a selectorFamily() that syncs with a
 * GraphQL Query or Subscription.  The family parameter or other upstream
 * atoms/selectors can be used to define the query variables or transform the
 * results.
 *
 * The selector is writable to act as a local cache of the server.
 * A GraphQL Mutation may also be provided to use the selector as a
 * write-through cache for updates to commit to the server.
 *
 * - `key` - Unique key string to identify this graphQLSelectorFamily
 * - `environment`: The Relay Environment or an EnvironmentKey to match with
 *   the environment provided with `<RecoilRelayEnvironemnt>`.
 * - `query` - GraphQL Query or Subscription
 * - `variables` - Callback to get the Variables to use for this query based
 *   on the family parameters and/or other upstream atoms or selectors.
 *   Return `null` to avoid the query if appropriate and use the `default` value.
 * - `default` - Optional default value to use if the query is skipped.
 * - `mapResponse` - Optional callback to transform the results based on the
 *   family parameters or upstream atoms or selectors.
 * - `mutations` - Optional GraphQL Mutation and variables to commit when the
 *   selector is written to.
 */


function graphQLSelectorFamily({
  key,
  environment,
  query,
  variables,
  mapResponse,
  mutations,
  ...options
}) {
  const internalAtoms = atomFamily({
    key,
    default: new DefaultValue(),
    effects: vars => [query.params.operationKind === 'query' ? RecoilRelay_graphQLQueryEffect({
      environment,
      variables: vars,
      // $FlowIssue[incompatible-call] Type is opaque, no way to refine
      query,
      mapResponse: response => ({
        source: 'remote',
        response
      })
    }) : RecoilRelay_graphQLSubscriptionEffect({
      environment,
      variables: vars,
      // $FlowIssue[incompatible-call] Type is opaque, no way to refine
      subscription: query,
      mapResponse: response => ({
        source: 'remote',
        response
      })
    }), mutations && RecoilRelay_graphQLMutationEffect({
      environment,
      mutation: mutations.mutation,
      variables: localUpdate => {
        if ( // commit mutation only if atom is updated locally
        localUpdate.source === 'local' && // Avoid mutation operation if user issued a reset and
        // did not provide a default value.
        !(localUpdate instanceof DefaultValue)) {
          const variablesIntermediate = mutations.variables(localUpdate.data);
          return typeof variablesIntermediate === 'function' ? variablesIntermediate(localUpdate.parameter) : variablesIntermediate;
        } else {
          return null;
        }
      }
    })].filter(Boolean)
  });

  function getVariables(parameter, get) {
    const variablesIntermediate = typeof variables === 'function' ? variables(parameter) : variables;
    return typeof variablesIntermediate === 'function' ? variablesIntermediate({
      get
    }) : variablesIntermediate;
  }

  const defaultValue = parameter => typeof options.default === 'function' ? // $FlowIssue[incompatible-use]
  options.default(parameter) : // $FlowIssue[incompatible-type]
  options.default;

  return selectorFamily({
    key: `${key}__Wrapper`,
    get: parameter => ({
      get
    }) => {
      const vars = getVariables(parameter, get);
      const result = get(internalAtoms(vars));

      if (result instanceof DefaultValue) {
        return 'default' in options ? defaultValue(parameter) : new Promise(() => {});
      }

      if (result.source === 'local') {
        return result.data;
      }

      const mapped = mapResponse(result.response, {
        get,
        variables: Recoil_nullthrows(vars)
      });
      return typeof mapped === 'function' ? // $FlowIssue[incompatible-use]
      mapped(parameter) : mapped;
    },
    set: parameter => ({
      set,
      get
    }, newValue) => set(internalAtoms(getVariables(parameter, get)), newValue instanceof DefaultValue ? 'default' in options ? {
      source: 'local',
      parameter,
      data: defaultValue(parameter)
    } : newValue : {
      source: 'local',
      parameter,
      data: newValue
    })
  });
}

var RecoilRelay_graphQLSelectorFamily = graphQLSelectorFamily;

/**
 * graphQLSelector() implements a Recoil selector that syncs with a
 * GraphQL Query or Subscription.  Other upstream  atoms/selectors can be used
 * to help define the query variables or transform the results.
 *
 * The selector is writable to act as a local cache of the server.
 * A GraphQL Mutation may also be provided to use the selector as a
 * write-through cache for updates to commit to the server.
 *
 * - `key` - Unique key string to identify this graphQLSelectorFamily
 * - `environment`: The Relay Environment or an EnvironmentKey to match with
 *   the environment provided with `<RecoilRelayEnvironemnt>`.
 * - `query` - GraphQL Query or Subscription
 * - `variables` - Callback to get the Variables to use for this query that may
 *   be based on other upstream atoms or selectors.
 *   Return `null` to avoid the query if appropriate and use the `default` value.
 * - `default` - Optional default value to use if the query is skipped.
 * - `mapResponse` - Optional callback to transform the results based on
 *   other upstream atoms or selectors.
 * - `mutations` - Optional GraphQL Mutation and variables to commit when the
 *   selector is written to.
 */


function graphQLSelector({
  variables,
  mutations,
  ...options
}) {
  return RecoilRelay_graphQLSelectorFamily({ ...options,
    variables: () => cbs => typeof variables === 'function' ? variables(cbs) : variables,
    mutations: mutations == null ? undefined : { ...mutations
    }
  })();
}

var RecoilRelay_graphQLSelector = graphQLSelector;

const {
  EnvironmentKey: EnvironmentKey$1,
  RecoilRelayEnvironment: RecoilRelayEnvironment$1,
  RecoilRelayEnvironmentProvider: RecoilRelayEnvironmentProvider$1,
  registerRecoilSnapshotRelayEnvironment: registerRecoilSnapshotRelayEnvironment$1
} = RecoilRelay_Environments$1;











var RecoilRelay_index = {
  EnvironmentKey: EnvironmentKey$1,
  RecoilRelayEnvironment: RecoilRelayEnvironment$1,
  RecoilRelayEnvironmentProvider: RecoilRelayEnvironmentProvider$1,
  registerRecoilSnapshotRelayEnvironment: registerRecoilSnapshotRelayEnvironment$1,
  graphQLQueryEffect: RecoilRelay_graphQLQueryEffect,
  graphQLSubscriptionEffect: RecoilRelay_graphQLSubscriptionEffect,
  graphQLMutationEffect: RecoilRelay_graphQLMutationEffect,
  graphQLSelector: RecoilRelay_graphQLSelector,
  graphQLSelectorFamily: RecoilRelay_graphQLSelectorFamily
};
var RecoilRelay_index_1 = RecoilRelay_index.EnvironmentKey;
var RecoilRelay_index_2 = RecoilRelay_index.RecoilRelayEnvironment;
var RecoilRelay_index_3 = RecoilRelay_index.RecoilRelayEnvironmentProvider;
var RecoilRelay_index_4 = RecoilRelay_index.registerRecoilSnapshotRelayEnvironment;
var RecoilRelay_index_5 = RecoilRelay_index.graphQLQueryEffect;
var RecoilRelay_index_6 = RecoilRelay_index.graphQLSubscriptionEffect;
var RecoilRelay_index_7 = RecoilRelay_index.graphQLMutationEffect;
var RecoilRelay_index_8 = RecoilRelay_index.graphQLSelector;
var RecoilRelay_index_9 = RecoilRelay_index.graphQLSelectorFamily;

export default RecoilRelay_index;
export { RecoilRelay_index_1 as EnvironmentKey, RecoilRelay_index_2 as RecoilRelayEnvironment, RecoilRelay_index_3 as RecoilRelayEnvironmentProvider, RecoilRelay_index_7 as graphQLMutationEffect, RecoilRelay_index_5 as graphQLQueryEffect, RecoilRelay_index_8 as graphQLSelector, RecoilRelay_index_9 as graphQLSelectorFamily, RecoilRelay_index_6 as graphQLSubscriptionEffect, RecoilRelay_index_4 as registerRecoilSnapshotRelayEnvironment };
